import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { ClipboardCheck, Truck } from 'lucide-react';
import { Modal } from '../../../../components/ui/Modal';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { FilterableSelect } from '../../../../components/ui/FilterableSelect';
import { AddressFields } from '../../../../components/address/AddressFields';
import { PickupScheduleFields } from '../../../../components/warehouses/PickupScheduleFields';
import {
  fetchPickupWarehouses,
  createPickupRequest,
  registerManualPickup,
} from '../../../../features/pickupRequests/pickupRequestsThunks';
import { clearPickupCreateError } from '../../../../features/pickupRequests/pickupRequestsSlice';
import {
  blankManualPickupForm,
  blankPickupRequestForm,
  pickupRequestFormFromWarehouse,
  validateManualPickupForm,
  validatePickupRequestForm,
} from '../../../../features/pickupRequests/pickupRequestsUtils';
import { COUNTRY_ISO2 } from '../../../../features/geo/geoConstants';
import { selectIsRunning } from '../../../../features/feedback/feedbackSlice';
import { toLocalDateString } from '../../../../utils/dates';

const inputClass =
  'w-full bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl px-4 py-2.5 text-sm text-ninja-dark dark:text-white outline-none focus:border-ninja-green transition-colors disabled:opacity-60';
const labelClass = 'block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5';
const fieldError = 'text-danger text-xs mt-1';

const MODES = { SCHEDULED: 'scheduled', MANUAL: 'manual' };

const modeOptions = (courierName) => [
  {
    value: MODES.SCHEDULED,
    label: 'Agendar retiro',
    description: `${courierName} crea el retiro con la dirección, la fecha y el horario que elijas.`,
  },
  {
    value: MODES.MANUAL,
    label: 'Registrar retiro manual',
    description: `Si ${courierName} no dejó crearlo y su soporte lo creó por ti, ingresa el ID de recogida que te dieron. No se envía nada a ${courierName}.`,
  },
];

const ModeChoice = ({ courierName, mode, onChange, disabled }) => (
  <div className="grid gap-2 sm:grid-cols-2">
    {modeOptions(courierName).map((option) => (
      <label
        key={option.value}
        data-testid="pickup-mode"
        data-mode={option.value}
        className={clsx(
          'flex items-start gap-3 rounded-xl border p-3 transition-colors',
          mode === option.value
            ? 'border-ninja-green bg-ninja-greenLight/40 dark:bg-ninja-green/10'
            : 'border-border hover:border-text-muted',
          disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
        )}
      >
        <input
          type="radio"
          name="pickup-mode"
          value={option.value}
          checked={mode === option.value}
          disabled={disabled}
          onChange={() => onChange(option.value)}
          className="mt-0.5 w-4 h-4 accent-ninja-green"
        />
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-ninja-dark dark:text-white">{option.label}</span>
          <span className="block text-xs text-text-secondary mt-0.5">{option.description}</span>
        </span>
      </label>
    ))}
  </div>
);

const WarehouseField = ({ value, onChange, options, disabled, error }) => {
  const { warehouses, loadingWarehouses, warehousesError } = useSelector((state) => state.pickupRequests);
  return (
    <div>
      <FilterableSelect
        variant="form"
        label="Bodega"
        testId="pickup-warehouse-select"
        value={value}
        onChange={onChange}
        options={options}
        placeholder={loadingWarehouses ? 'Cargando bodegas...' : 'Elige una bodega'}
        searchPlaceholder="Buscar bodega..."
        disabled={loadingWarehouses || disabled}
        hasError={Boolean(error)}
      />
      {error && <p className={fieldError}>{error}</p>}
      {warehousesError && (
        <p data-testid="pickup-warehouses-error" className="text-sm text-danger mt-2">
          {warehousesError}
        </p>
      )}
      {/* Without `!warehousesError`, a failed load claimed the company has none. */}
      {!loadingWarehouses && !warehousesError && warehouses.length === 0 && (
        <p data-testid="pickup-empty-warehouses" className="text-sm text-text-secondary mt-2">
          Tu empresa no tiene bodegas para retirar.
        </p>
      )}
    </div>
  );
};

/**
 * Two ways to put a pickup on record with one courier:
 *
 * - **Scheduled**: for today or a later day, from one of the company's
 *   warehouses. `POST pickup-requests/` calls the courier's API, so it books a
 *   real pickup; a refusal is kept as a failed request and explained here.
 * - **Manual**: one the courier's support created by phone when its API would
 *   not. Only its id and date are stored, and nothing is sent to the courier.
 *
 * Each mode keeps its own form, so switching does not lose what was typed.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} props.courier - The card it was opened from.
 */
const CreatePickupRequestDialog = ({ isOpen, onClose, courier }) => {
  const dispatch = useDispatch();
  const { warehouses, createError } = useSelector((state) => state.pickupRequests);
  const scheduling = useSelector(selectIsRunning(createPickupRequest));
  const registering = useSelector(selectIsRunning(registerManualPickup));
  const busy = scheduling || registering;

  const [today] = useState(() => toLocalDateString(new Date()));
  const [mode, setMode] = useState(MODES.SCHEDULED);
  const [form, setForm] = useState(() => blankPickupRequestForm(today, courier));
  const [manualForm, setManualForm] = useState(() => blankManualPickupForm(today, courier));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(fetchPickupWarehouses(courier));
    return () => {
      dispatch(clearPickupCreateError());
    };
  }, [dispatch, courier]);

  const courierName = courier.toUpperCase();
  const shownErrors = { ...(createError?.fieldErrors || {}), ...errors };

  const warehouseOptions = useMemo(
    () => warehouses.map((warehouse) => ({ value: warehouse.id, label: warehouse.name })),
    [warehouses]
  );

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setErrors({});
    dispatch(clearPickupCreateError());
  };

  const handleWarehouseChange = (warehouseId) => {
    const warehouse = warehouses.find(({ id }) => id === warehouseId);
    if (!warehouse) return;
    setForm(pickupRequestFormFromWarehouse(warehouse, today, courier));
    setErrors({});
    dispatch(clearPickupCreateError());
  };

  // Stable: `AddressFields` runs an effect on it.
  const handleAddressChange = useCallback(
    (patch) => setForm((prev) => ({ ...prev, address: { ...prev.address, ...patch } })),
    []
  );

  const handleScheduledSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validatePickupRequestForm(form, today);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const action = await dispatch(createPickupRequest(form));
    if (action.meta.requestStatus === 'fulfilled') onClose();
  };

  const handleManualSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateManualPickupForm(manualForm, today, { needsWarehouse: false });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const action = await dispatch(registerManualPickup(manualForm));
    if (action.meta.requestStatus === 'fulfilled') onClose();
  };

  const noWarehouse = !form.warehouseId;
  const locked = noWarehouse || busy;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={`Solicitud de retiro — ${courierName}`}
      testId="pickup-create-modal"
      busy={busy}
    >
      <div className="space-y-6">
        <ModeChoice courierName={courierName} mode={mode} onChange={handleModeChange} disabled={busy} />

        {createError && (
          <div
            role="alert"
            data-testid="pickup-create-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {createError.message}
            {createError.saved && ' Quedó registrada como fallida en el listado.'}
          </div>
        )}

        {mode === MODES.SCHEDULED ? (
          <form data-testid="pickup-create-form" noValidate onSubmit={handleScheduledSubmit} className="space-y-6">
            <WarehouseField
              value={form.warehouseId}
              onChange={handleWarehouseChange}
              options={warehouseOptions}
              disabled={busy}
              error={shownErrors.warehouseId}
            />

            {/* A native fieldset disables every control inside, the selects' buttons included. */}
            <fieldset disabled={locked} aria-disabled={locked} className="space-y-6 disabled:opacity-60">
              <div className="space-y-4">
                <AddressFields
                  country={COUNTRY_ISO2.CL}
                  value={form.address}
                  onChange={handleAddressChange}
                  errors={shownErrors}
                />
                <div>
                  <label htmlFor="pickup-references" className={labelClass}>
                    Referencias (opcional)
                  </label>
                  <textarea
                    id="pickup-references"
                    data-testid="pickup-references"
                    rows={2}
                    value={form.references}
                    onChange={(e) => setForm((prev) => ({ ...prev, references: e.target.value }))}
                    className={inputClass}
                  />
                  {shownErrors.references && <p className={fieldError}>{shownErrors.references}</p>}
                </div>
              </div>

              <div className="max-w-xs">
                <label htmlFor="pickup-date" className={labelClass}>
                  Fecha de retiro
                </label>
                <input
                  id="pickup-date"
                  data-testid="pickup-date"
                  type="date"
                  min={today}
                  value={form.requestedDate}
                  onChange={(e) => setForm((prev) => ({ ...prev, requestedDate: e.target.value }))}
                  className={inputClass}
                />
                {shownErrors.requestedDate && <p className={fieldError}>{shownErrors.requestedDate}</p>}
              </div>

              <PickupScheduleFields
                idPrefix="pickup-schedule"
                {...form.schedule}
                onChange={(patch) => setForm((prev) => ({ ...prev, schedule: { ...prev.schedule, ...patch } }))}
                errors={shownErrors}
                disabled={locked}
              />
            </fieldset>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={onClose} disabled={busy}>
                Cancelar
              </Button>
              <PendingButton
                type="submit"
                pendingOn={createPickupRequest}
                variant="primary"
                icon={Truck}
                loadingLabel="Agendando..."
                disabled={noWarehouse}
                data-testid="pickup-create-submit"
              >
                Agendar retiro
              </PendingButton>
            </div>
          </form>
        ) : (
          <form data-testid="pickup-manual-form" noValidate onSubmit={handleManualSubmit} className="space-y-6">
            <fieldset disabled={busy} className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="pickup-external-id" className={labelClass}>
                  ID de recogida
                </label>
                <input
                  id="pickup-external-id"
                  data-testid="pickup-external-id"
                  value={manualForm.externalPickupId}
                  onChange={(e) => setManualForm((prev) => ({ ...prev, externalPickupId: e.target.value }))}
                  className={inputClass}
                />
                {shownErrors.externalPickupId && <p className={fieldError}>{shownErrors.externalPickupId}</p>}
              </div>
              <div>
                <label htmlFor="pickup-scheduled-date" className={labelClass}>
                  Fecha programada
                </label>
                <input
                  id="pickup-scheduled-date"
                  data-testid="pickup-scheduled-date"
                  type="date"
                  min={today}
                  value={manualForm.scheduledDate}
                  onChange={(e) => setManualForm((prev) => ({ ...prev, scheduledDate: e.target.value }))}
                  className={inputClass}
                />
                {shownErrors.scheduledDate && <p className={fieldError}>{shownErrors.scheduledDate}</p>}
              </div>
            </fieldset>

            {/* The hours the courier's support agreed to, not the warehouse's. */}
            <PickupScheduleFields
              idPrefix="pickup-manual-schedule"
              {...manualForm.schedule}
              onChange={(patch) =>
                setManualForm((prev) => ({ ...prev, schedule: { ...prev.schedule, ...patch } }))
              }
              errors={shownErrors}
              disabled={busy}
            />

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={onClose} disabled={busy}>
                Cancelar
              </Button>
              <PendingButton
                type="submit"
                pendingOn={registerManualPickup}
                variant="primary"
                icon={ClipboardCheck}
                loadingLabel="Registrando..."
                data-testid="pickup-manual-submit"
              >
                Registrar retiro
              </PendingButton>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

/** Mounted on opening, so each opening starts from blank forms. */
export const CreatePickupRequestModal = (props) =>
  props.isOpen ? <CreatePickupRequestDialog {...props} /> : null;
